import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    Button,
    Select,
    MenuItem,
    FormControl,
    Modal,
    Fade,
    Backdrop,
  } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useDropzone } from 'react-dropzone';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import API_BASE_URL from '../../config';
import './AdminMedia.css';

const AdminMedia = () => {
  const [mediaContent, setMediaContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMediaContent();
    fetchCustomers();
  }, []);

    const handleOpenModal = (imageUrl) => {
        setSelectedImage(imageUrl);
        setOpenModal(true);
}   ;

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedImage(null);
    };

  const fetchMediaContent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/media-files`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMediaContent(data);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching media files:", error);
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/customers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const onDrop = useCallback(acceptedFiles => {
    setUploadedFiles(acceptedFiles.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      status: 'ready',
      customerId: -1 // Default to 'All' option for all files
    })));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleUpload = async (fileInfo) => {
    const selectedCustomer = fileInfo.customerId === -1 
      ? { id: -1, org_name: 'All' } 
      : customers.find(customer => customer.id === fileInfo.customerId);
  
    console.log('Uploading file:', {
      fileName: fileInfo.file.name,
      fileType: fileInfo.file.type,
      fileSize: fileInfo.file.size,
      customer: {
        id: selectedCustomer.id,
        name: selectedCustomer.org_name
      }
    });
  
    const formData = new FormData();
    formData.append('file', fileInfo.file);
    formData.append('customerId', fileInfo.customerId);
  
    try {
      fileInfo.status = 'uploading';
      setUploadedFiles([...uploadedFiles]);
  
      const response = await fetch(`${API_BASE_URL}/admin/upload-media`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result = await response.json();
      console.log('File upload complete:', result);
  
      // Update the fileInfo with the ID from the server
      fileInfo.status = 'success';
      fileInfo.id = result.file.id;
  
      // Update the state to reflect the new ID
      setUploadedFiles([...uploadedFiles]);
  
    } catch (error) {
      console.error("Error uploading file:", error);
      fileInfo.status = 'failed';
    } finally {
      setUploadedFiles([...uploadedFiles]);
    }
  };
  
  
  
  

  const handleDelete = (fileToDelete) => {
    setUploadedFiles(uploadedFiles.filter(file => file !== fileToDelete));
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchMediaContent();
  };

  const handleCustomerChange = (event, fileInfo) => {
    const updatedFiles = uploadedFiles.map(file => 
      file === fileInfo ? { ...file, customerId: event.target.value } : file
    );
    setUploadedFiles(updatedFiles);
  };

  const isFileDuplicate = (fileName) => {
    return mediaContent.some(media => media.file_name === `s:${fileName}`);
  };


  return (
    <Container className="admin-media-container">
      <Typography variant="h4" gutterBottom>
        Admin Media
      </Typography>
      <Box 
        {...getRootProps()} 
        className="dropzone"
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <Typography variant="h6">Drop the files here ...</Typography>
        ) : (
          <Typography variant="h6">
            Drag and drop files here, or click to select files
          </Typography>
        )}
        <Button className="upload-button" startIcon={<CloudUploadIcon />}>
          Select Files
        </Button>
      </Box>
  
      {uploadedFiles.length > 0 && (
        <TableContainer component={Paper} className="uploads-table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell >Preview</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploadedFiles.map((fileInfo, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {fileInfo.preview ? (
                      <img src={fileInfo.preview} alt={fileInfo.file.name} className="preview-image" />
                    ) : (
                      <Typography>Preview not available</Typography>
                    )}
                  </TableCell>
                  <TableCell>{fileInfo.file.name}</TableCell>
                  <TableCell>
                    <FormControl fullWidth>
                      <Select
                        value={fileInfo.customerId}
                        onChange={(event) => handleCustomerChange(event, fileInfo)}
                      >
                        <MenuItem value={-1}>All</MenuItem>
                        {customers.map((customer) => (
                          <MenuItem key={customer.id} value={customer.id}>
                            {customer.org_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {isFileDuplicate(fileInfo.file.name) ? (
                        <span className="file-exists-status">File Exists</span>
                    ) : (
                        fileInfo.status
                    )}
                    </TableCell>
                  <TableCell className="actions-cell">
                    <div className="action-buttons">
                        <Button 
                        className="upload-button"
                        onClick={() => handleUpload(fileInfo)} 
                        disabled={fileInfo.status === 'uploading' || isFileDuplicate(fileInfo.file.name)}
                        startIcon={<CloudUploadIcon />}
                        style={{
                            backgroundColor: isFileDuplicate(fileInfo.file.name) ? 'grey' : '',
                            color: isFileDuplicate(fileInfo.file.name) ? 'white' : ''
                        }}
                        >
                        UPLOAD
                        </Button>
                      <Button 
                        className="delete-button"
                        onClick={() => handleDelete(fileInfo)}
                        startIcon={<DeleteIcon />}
                      >
                        UNSELECT
                      </Button>
                      </div>
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
    )}
  
  <Box className="existing-media-header">
        <Typography variant="h5">Existing Media</Typography>
        <Button className="refresh-button" onClick={handleRefresh} startIcon={<RefreshIcon />}>
          Refresh
        </Button>
      </Box>
      <TableContainer component={Paper} className="media-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Preview</TableCell>
              <TableCell>File Name</TableCell>
              <TableCell>User</TableCell>
              <TableCell>File Size</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : mediaContent.length > 0 ? (
              mediaContent.map((media) => (
                <TableRow key={media.id}>
                  <TableCell>
                    {media.file_name.match(/\.(jpeg|jpg|gif|png)$/) ? (
                      <img 
                        src={`${API_BASE_URL}${media.imageUrl}`} 
                        alt={media.file_name} 
                        className="preview-image" 
                      />
                    ) : (
                      <Typography>Preview not available</Typography>
                    )}
                  </TableCell>
                  <TableCell>{media.file_name}</TableCell>
                  <TableCell>{media.user_name || 'N/A'}</TableCell>
                  <TableCell>{(media.file_size / 1024).toFixed(2)} KB</TableCell>

                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No media found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};


export default AdminMedia;